import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Play, RefreshCw, Loader2 } from 'lucide-react';
import Editor from '@monaco-editor/react';
import { useCreateTool, useUpdateTool } from '../hooks';
import toolsService from '../services/toolsService';
import Toast from '../components/Toast';
import Button from '../components/Button';
import PageHeader from '../components/ui/PageHeader';
import { BackLink } from '../components/ui/ui.styles';
import {
  AddRowBtn,
  DefCard,
  DefCardHead,
  EmptyHint,
  FieldBlock,
  FieldGrid,
  FooterActions,
  HeaderRow,
  InlineActions,
  LoadingState,
  MetaCard,
  MetaGrid,
  MonacoWrap,
  OutputBox,
  SectionHead,
  ToolBuilderShell,
  ToolInput,
  ToolSection,
  ToolSections,
  ToolSelect,
  ToolTextarea,
} from '../components/ToolsSection/ToolBuilder.styles';

const toolTypes = ['API', 'CODE'];
const httpMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const parameterTypes = ['String', 'Number', 'Boolean', 'Array', 'Object'];
const languageOptions = [{ label: 'Python', value: 'python' }];

const languageMap = {
  javascript: 63,
  typescript: 74,
  python: 71,
  c: 50,
  cpp: 54,
  java: 62,
  php: 68,
};

let uid = 0;
const makeId = () => `row_${Date.now()}_${uid++}`;
const emptyDef = () => ({ id: makeId(), name: '', type: '', desc: '', defaultValue: '', required: 'false' });

function Section({ title, description, action, children }) {
  return (
    <ToolSection>
      <SectionHead>
        <div>
          <h3>{title}</h3>
          {description ? <p>{description}</p> : null}
        </div>
        {action}
      </SectionHead>
      {children}
    </ToolSection>
  );
}

function Field({ label, required, children }) {
  return (
    <FieldBlock>
      <label>
        {required ? <span className="req">*</span> : null}
        {label}
      </label>
      {children}
    </FieldBlock>
  );
}

function DefinitionList({ label, items, setItems }) {
  const update = (id, field, value) => {
    setItems(items.map(item => (item.id === id ? { ...item, [field]: value } : item)));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {items.map((item, index) => (
        <DefCard key={item.id}>
          <DefCardHead>
            <span>
              {label} {index + 1}
            </span>
            <button type="button" onClick={() => setItems(items.filter(i => i.id !== item.id))} aria-label="Remove">
              <Trash2 size={16} />
            </button>
          </DefCardHead>
          <FieldGrid>
            <Field label={`${label} Name`}>
              <ToolInput
                value={item.name}
                onChange={e => update(item.id, 'name', e.target.value)}
                placeholder={`Enter ${label.toLowerCase()} name`}
              />
            </Field>
            <Field label="Type">
              <ToolSelect value={item.type} onChange={e => update(item.id, 'type', e.target.value)}>
                <option value="">Select type</option>
                {parameterTypes.map(t => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </ToolSelect>
            </Field>
            <Field label="Default Value">
              <ToolInput
                value={item.defaultValue}
                onChange={e => update(item.id, 'defaultValue', e.target.value)}
                placeholder="Enter default value"
              />
            </Field>
            <Field label="Required">
              <ToolSelect value={item.required} onChange={e => update(item.id, 'required', e.target.value)}>
                <option value="true">True</option>
                <option value="false">False</option>
              </ToolSelect>
            </Field>
          </FieldGrid>
          <Field label="Description">
            <ToolInput
              value={item.desc}
              onChange={e => update(item.id, 'desc', e.target.value)}
              placeholder={`Describe this ${label.toLowerCase()}...`}
            />
          </Field>
        </DefCard>
      ))}
    </div>
  );
}

function validateAndConvert(type, rawValue) {
  switch (type) {
    case 'Number':
      if (isNaN(Number(rawValue))) return { valid: false };
      return { valid: true, value: Number(rawValue) };
    case 'Boolean': {
      const normalized = String(rawValue).toLowerCase();
      if (!['true', 'false'].includes(normalized)) return { valid: false };
      return { valid: true, value: normalized === 'true' };
    }
    case 'Array':
      try {
        const parsed = JSON.parse(rawValue);
        if (!Array.isArray(parsed)) return { valid: false };
        return { valid: true, value: parsed };
      } catch {
        return { valid: false };
      }
    case 'Object':
      try {
        const parsed = JSON.parse(rawValue);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return { valid: false };
        return { valid: true, value: parsed };
      } catch {
        return { valid: false };
      }
    case 'String':
    default:
      return { valid: true, value: rawValue };
  }
}

function buildHeadersObject(headers) {
  return headers.reduce((acc, h) => {
    if (h.key && h.value) {
      let uniqueKey = h.key;
      let counter = 2;
      while (acc[uniqueKey]) {
        uniqueKey = `${h.key}${counter}`;
        counter++;
      }
      acc[uniqueKey] = h.value;
    }
    return acc;
  }, {});
}

const buildDefList = items =>
  items
    .filter(item => item.name && item.type)
    .map(item => ({
      name: item.name,
      desc: item.desc,
      required: item.required,
      type: item.type,
      default_value: item.defaultValue || null,
    }));

export default function CreateToolPage({ toolId: toolIdProp }) {
  const { id: routeId } = useParams();
  const toolId = toolIdProp || routeId;
  const navigate = useNavigate();
  const isEdit = !!toolId;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [toolType, setToolType] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [method, setMethod] = useState('');
  const [headers, setHeaders] = useState([]);
  const [params, setParams] = useState([]);
  const [body, setBody] = useState([]);

  const [language, setLanguage] = useState('python');
  const [code, setCode] = useState('');
  const [running, setRunning] = useState(false);
  const [executionOutput, setExecutionOutput] = useState('');

  const [testSnapshot, setTestSnapshot] = useState(null);
  const [testValues, setTestValues] = useState({});
  const [testing, setTesting] = useState(false);
  const [testOutput, setTestOutput] = useState(null);

  const [detailLoading, setDetailLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return undefined;
    (async () => {
      try {
        setDetailLoading(true);
        const resp = await toolsService.getToolDetails(toolId);
        const detail = resp?.detail;
        if (!detail) return;
        setName(detail.tool_name || '');
        setDescription(detail.description || '');
        setToolType(detail.tool_category || '');
        setEndpoint(detail.url || '');
        setMethod(detail.method || '');
        setHeaders(Object.entries(detail.headers || {}).map(([key, value]) => ({ id: makeId(), key, value })));
        setParams(
          (detail.params || []).map(p => ({
            id: makeId(),
            name: p.name || '',
            type: p.type || '',
            desc: p.desc || '',
            defaultValue: p.default_value ?? '',
            required: String(p.required ?? 'false'),
          })),
        );
        setBody(
          (detail.body || []).map(b => ({
            id: makeId(),
            name: b.name || '',
            type: b.type || '',
            desc: b.desc || '',
            defaultValue: b.default_value ?? '',
            required: String(b.required ?? 'false'),
          })),
        );
      } catch (error) {
        Toast({ type: 'error', message: error?.message || 'Failed to load tool' });
      } finally {
        setDetailLoading(false);
      }
    })();
    return undefined;
  }, [isEdit, toolId]);

  const onMutationSuccess = () => {
    Toast({ type: 'success', message: isEdit ? 'Tool updated successfully' : 'Tool created successfully' });
    navigate('/tools');
  };
  const onMutationError = err => {
    Toast({ type: 'error', message: err?.detail || err?.message || 'Something went wrong' });
  };

  const createTool = useCreateTool({ onSuccess: onMutationSuccess, onError: onMutationError });
  const updateTool = useUpdateTool({ onSuccess: onMutationSuccess, onError: onMutationError });

  const handleSave = () => {
    if (!name.trim()) {
      Toast({ type: 'error', message: 'Tool name is required' });
      return;
    }
    if (!toolType) {
      Toast({ type: 'error', message: 'Tool Type is required' });
      return;
    }
    if (!description.trim()) {
      Toast({ type: 'error', message: 'Description is required' });
      return;
    }

    const payload = {
      ...(isEdit ? { tool_id: toolId } : {}),
      tool_name: name,
      description,
      tool_category: toolType,
      tool_type: 'user_created',
      url: endpoint,
      method,
      headers: buildHeadersObject(headers),
      params: buildDefList(params),
      body: buildDefList(body),
    };

    if (isEdit) updateTool.mutate(payload);
    else createTool.mutate(payload);
  };

  const handleFetchTestData = () => {
    setTestSnapshot({ endpoint, method, headers: [...headers], params: [...params], body: [...body] });
    const values = {};
    params.forEach(p => {
      values[`param_${p.id}`] = p.defaultValue ?? '';
    });
    body.forEach(b => {
      values[`body_${b.id}`] = b.defaultValue ?? '';
    });
    setTestValues(values);
  };

  const handleTest = async () => {
    if (!testSnapshot) {
      Toast({ type: 'info', message: 'Fetch data before testing the tool' });
      return;
    }
    setTesting(true);

    const paramsObj = {};
    for (const p of testSnapshot.params) {
      const raw = testValues[`param_${p.id}`];
      if (!p.name || raw === undefined || raw === '') continue;
      const { valid, value } = validateAndConvert(p.type, raw);
      if (!valid) {
        Toast({ type: 'error', message: `Invalid type for parameter "${p.name}". Expected ${p.type}.` });
        continue;
      }
      let uniqueKey = p.name;
      let counter = 2;
      while (uniqueKey in paramsObj) {
        uniqueKey = `${p.name}${counter}`;
        counter++;
      }
      paramsObj[uniqueKey] = value;
    }

    const bodyObj = {};
    for (const b of testSnapshot.body) {
      const raw = testValues[`body_${b.id}`];
      if (!b.name || raw === undefined || raw === '') continue;
      const { valid, value } = validateAndConvert(b.type, raw);
      if (!valid) {
        Toast({ type: 'error', message: `Invalid type for body parameter "${b.name}". Expected ${b.type}.` });
        continue;
      }
      let uniqueKey = b.name;
      let counter = 2;
      while (uniqueKey in bodyObj) {
        uniqueKey = `${b.name}${counter}`;
        counter++;
      }
      bodyObj[uniqueKey] = value;
    }

    try {
      const res = await toolsService.testTool({
        tool_name: name,
        description,
        tool_category: toolType,
        tool_type: 'user_created',
        url: testSnapshot.endpoint,
        method: testSnapshot.method,
        headers: buildHeadersObject(testSnapshot.headers),
        params: paramsObj,
        body: bodyObj,
      });
      setTestOutput(res);
    } catch (error) {
      Toast({ type: 'error', message: error.message });
      setTestOutput(error);
    } finally {
      setTesting(false);
    }
  };

  const handleRun = async () => {
    setRunning(true);
    setExecutionOutput('Running your code...');
    try {
      const response = await fetch('https://ce.judge0.com/submissions?base64_encoded=false&wait=true', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          language_id: languageMap[language],
          source_code: code,
          stdin: '',
        }),
      });
      const result = await response.json();
      if (result.stderr) setExecutionOutput(result.stderr);
      else if (result.compile_output) setExecutionOutput(result.compile_output);
      else setExecutionOutput(result.stdout || 'No output');
    } catch (error) {
      setExecutionOutput(`Error: ${error.message}`);
    }
    setRunning(false);
  };

  const saving = createTool.isPending || updateTool.isPending;

  return (
    <ToolBuilderShell>
      <BackLink type="button" onClick={() => navigate('/tools')}>
        <ArrowLeft size={16} /> Back to Tools
      </BackLink>

      <PageHeader
        title={isEdit ? 'Edit Tool' : 'Create Tool'}
        subtitle="Define a tool with connection parameters so agents can call it during workflows."
      />

      {detailLoading ? (
        <LoadingState>
          <div className="loading-row">
            <Loader2 size={16} className="spin" /> Loading tool details...
          </div>
          {[0, 1, 2].map(i => (
            <div className="skeleton" key={i}>
              <div className="line" />
              <div className="grid">
                <div className="box" />
                <div className="box" />
              </div>
              <div className="area" />
            </div>
          ))}
        </LoadingState>
      ) : (
        <ToolSections>
          <Section title="Basics">
            <FieldGrid>
              <Field label="Tool Name" required>
                <ToolInput value={name} onChange={e => setName(e.target.value)} placeholder="Name your tool" />
              </Field>
              <Field label="Tool Type" required>
                <ToolSelect value={toolType} onChange={e => setToolType(e.target.value)}>
                  <option value="">Select tool type</option>
                  {toolTypes.map(t => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </ToolSelect>
              </Field>
            </FieldGrid>
            <Field label="Description" required>
              <ToolTextarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                placeholder="What does this tool do and when should an agent use it?"
              />
            </Field>
          </Section>

          {toolType === 'API' && (
            <>
              <Section title="API Configuration" description="Configure the API endpoint and request method">
                <FieldGrid $cols={4}>
                  <Field label="API Endpoint">
                    <ToolInput
                      value={endpoint}
                      onChange={e => setEndpoint(e.target.value)}
                      placeholder="https://api.example.com/endpoint"
                    />
                  </Field>
                  <Field label="HTTP Method">
                    <ToolSelect value={method} onChange={e => setMethod(e.target.value)}>
                      <option value="">Select method</option>
                      {httpMethods.map(m => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </ToolSelect>
                  </Field>
                </FieldGrid>
              </Section>

              <Section
                title="Request Headers"
                description="Custom headers to include with the request (e.g. Content-Type)."
                action={
                  <AddRowBtn
                    type="button"
                    onClick={() => setHeaders([...headers, { id: makeId(), key: '', value: '' }])}>
                    <Plus size={14} /> Add header
                  </AddRowBtn>
                }>
                {headers.length === 0 ? (
                  <EmptyHint>No headers added.</EmptyHint>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {headers.map(h => (
                      <HeaderRow key={h.id}>
                        <ToolInput
                          value={h.key}
                          onChange={e =>
                            setHeaders(headers.map(x => (x.id === h.id ? { ...x, key: e.target.value } : x)))
                          }
                          placeholder="Authorization"
                        />
                        <ToolInput
                          value={h.value}
                          onChange={e =>
                            setHeaders(headers.map(x => (x.id === h.id ? { ...x, value: e.target.value } : x)))
                          }
                          placeholder="Bearer token 123"
                        />
                        <button type="button" onClick={() => setHeaders(headers.filter(x => x.id !== h.id))}>
                          <Trash2 size={16} />
                        </button>
                      </HeaderRow>
                    ))}
                  </div>
                )}
              </Section>

              <Section
                title="Parameters"
                description="Define the parameters that this tool accepts"
                action={
                  <AddRowBtn type="button" onClick={() => setParams([...params, emptyDef()])}>
                    <Plus size={14} /> Add parameter
                  </AddRowBtn>
                }>
                {params.length === 0 ? (
                  <EmptyHint>No parameters added.</EmptyHint>
                ) : (
                  <DefinitionList label="Parameter" items={params} setItems={setParams} />
                )}
              </Section>

              <Section
                title="Body"
                description="Define the body that this tool accepts"
                action={
                  <AddRowBtn type="button" onClick={() => setBody([...body, emptyDef()])}>
                    <Plus size={14} /> Add body field
                  </AddRowBtn>
                }>
                {body.length === 0 ? (
                  <EmptyHint>No body fields added.</EmptyHint>
                ) : (
                  <DefinitionList label="Body" items={body} setItems={setBody} />
                )}
              </Section>
            </>
          )}

          {toolType === 'CODE' && (
            <Section title="Code" description="Write the code this tool runs">
              <Field label="Language">
                <ToolSelect value={language} onChange={e => setLanguage(e.target.value)}>
                  {languageOptions.map(l => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </ToolSelect>
              </Field>
              <MonacoWrap>
                <Editor
                  height="250px"
                  theme="light"
                  language={language}
                  value={code}
                  onChange={value => setCode(value)}
                  options={{
                    lineNumbers: 'on',
                    minimap: { enabled: false },
                    fontSize: 14,
                    lineHeight: 22,
                    padding: { top: 16, bottom: 16 },
                    smoothScrolling: true,
                    stickyScroll: { enabled: false },
                  }}
                />
              </MonacoWrap>
              <Field label="Output">
                <OutputBox>{executionOutput || 'Run the code to see output.'}</OutputBox>
              </Field>
              <InlineActions>
                <Button type="button" width="140px" onClick={handleRun} loader={running}>
                  <Play size={16} /> Test Code
                </Button>
              </InlineActions>
            </Section>
          )}

          {toolType === 'API' && (
            <Section
              title="Test Tool"
              description="Make sure to fill all the fields and fetch data before testing tool"
              action={
                <AddRowBtn type="button" onClick={handleFetchTestData}>
                  <RefreshCw size={14} /> Fetch Data
                </AddRowBtn>
              }>
              <MetaGrid>
                <MetaCard>
                  <p className="label">API Endpoint</p>
                  <p className="value">{testSnapshot?.endpoint || 'Null'}</p>
                </MetaCard>
                <MetaCard>
                  <p className="label">HTTP Method</p>
                  <p className="value">{testSnapshot?.method || 'Null'}</p>
                </MetaCard>
              </MetaGrid>

              {testSnapshot?.headers?.length > 0 && (
                <div>
                  <EmptyHint style={{ marginBottom: 8 }}>Headers</EmptyHint>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {testSnapshot.headers.map(h => (
                      <p key={h.id} style={{ margin: 0, fontSize: 14, color: 'var(--ink)' }}>
                        <span style={{ color: 'var(--quiet)' }}>{h.key || 'Null'}: </span>
                        {h.value || 'Null'}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {testSnapshot?.params?.length > 0 && (
                <div>
                  <EmptyHint style={{ marginBottom: 8 }}>Parameters</EmptyHint>
                  <FieldGrid>
                    {testSnapshot.params.map(p => (
                      <Field key={p.id} label={p.name || 'Unnamed parameter'}>
                        <ToolInput
                          value={testValues[`param_${p.id}`] ?? ''}
                          onChange={e => setTestValues(v => ({ ...v, [`param_${p.id}`]: e.target.value }))}
                          placeholder="Enter value to proceed"
                        />
                      </Field>
                    ))}
                  </FieldGrid>
                </div>
              )}

              {testSnapshot?.body?.length > 0 && (
                <div>
                  <EmptyHint style={{ marginBottom: 8 }}>Body</EmptyHint>
                  <FieldGrid>
                    {testSnapshot.body.map(b => (
                      <Field key={b.id} label={b.name || 'Unnamed field'}>
                        <ToolInput
                          value={testValues[`body_${b.id}`] ?? ''}
                          onChange={e => setTestValues(v => ({ ...v, [`body_${b.id}`]: e.target.value }))}
                          placeholder="Enter value to proceed"
                        />
                      </Field>
                    ))}
                  </FieldGrid>
                </div>
              )}

              <Field label="Output">
                <OutputBox>
                  {testOutput
                    ? JSON.stringify(testOutput?.data ?? testOutput, null, 2)
                    : 'Fetch data and run a test to see output.'}
                </OutputBox>
              </Field>

              <InlineActions>
                <Button type="button" width="140px" onClick={handleTest} loader={testing}>
                  <Play size={16} /> Test Tool
                </Button>
              </InlineActions>
            </Section>
          )}
        </ToolSections>
      )}

      <FooterActions>
        <Button type="button" width="120px" variant="secondary-outline" onClick={() => navigate('/tools')}>
          Cancel
        </Button>
        <Button type="button" width="160px" onClick={handleSave} loader={saving} disabled={saving || detailLoading}>
          <Save size={16} />
          {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Tool'}
        </Button>
      </FooterActions>
    </ToolBuilderShell>
  );
}
